import { useState } from 'react';
import { Badge, Button, Collapse, Loader, ThemeIcon, useComputedColorScheme } from '@mantine/core';
import { IconCheck, IconChevronDown, IconChevronRight, IconCircleDot, IconPlayerStopFilled, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { AIChatMessage } from '@domains/aiAssistant';
import type { HumanStepPayload, StepExecutionStatus } from '@infrastructure/aiAssistant/types';
import { useAIAssistantActions } from '@domains/aiAssistant';
import classes from './CommandPlanCard.module.css';
import { componentsTestIds } from '@components/testids';

interface CommandPlanCardProps {
  message: AIChatMessage;
}

function StepStatusIcon({ status }: { status: StepExecutionStatus }) {
  switch ( status ) {
    case 'pending':
      return <IconCircleDot size={14} color="var(--mantine-color-dimmed)" />;
    case 'executing':
      return <Loader size={14} />;
    case 'completed':
      return (
        <ThemeIcon size={16} radius="xl" color="primary" variant="light">
          <IconCheck size={10} />
        </ThemeIcon>
      );
    case 'failed':
      return (
        <ThemeIcon size={16} radius="xl" color="red" variant="light">
          <IconX size={10} />
        </ThemeIcon>
      );
    case 'skipped':
      return <IconCircleDot size={14} color="var(--mantine-color-dimmed)" />;
    default:
      return <IconCircleDot size={14} color="var(--mantine-color-dimmed)" />;
  }
}

function getPlanStatusColor( status: AIChatMessage['planStatus']): string {
  switch ( status ) {
    case 'completed': return 'primary';
    case 'failed': return 'red';
    case 'aborted': return 'gray';
    case 'discarded': return 'gray';
    case 'executing': return 'blue.4';
    default: return 'gray';
  }
}

function getPlanStatusKey( status: AIChatMessage['planStatus']): string {
  switch ( status ) {
    case 'completed': return 'ai_assistant_plan_done';
    case 'failed': return 'ai_assistant_plan_failed';
    case 'aborted': return 'ai_assistant_plan_aborted';
    case 'discarded': return 'ai_assistant_plan_discarded';
    case 'executing': return 'ai_assistant_plan_running';
    case 'pending': return 'ai_assistant_plan_ready';
    default: return '';
  }
}

export function CommandPlanCard({ message }: CommandPlanCardProps ) {
  const colorScheme = useComputedColorScheme( 'light' );
  const { executePlan, discardPlan, abortPlan, completeHumanStep } = useAIAssistantActions();
  const { t } = useTranslation( 'interactions' );
  const [ expanded, setExpanded ] = useState( true );

  const plan = message.commandPlan;
  if ( !plan ) return null;

  const { planStatus, executionResults } = message;
  const stepCount = plan.steps.length;
  const isFinished = planStatus === 'completed' || planStatus === 'failed' || planStatus === 'aborted' || planStatus === 'discarded';

  return (
    <div className={classes.commandPlanCard}>
      <div className={classes.planHeader} onClick={() => setExpanded( !expanded )}>
        <div className={classes.planHeaderLeft}>
          {expanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
          <span className={classes.planTitle}>{plan.title}</span>
          <Badge size="xs" variant="light" color="gray">{stepCount} {stepCount !== 1 ? t( 'ai_assistant_steps' ) : t( 'ai_assistant_step' )}</Badge>
        </div>
        {planStatus && planStatus !== 'pending' && (
          <Badge size="xs" variant="light" color={getPlanStatusColor( planStatus )}>
            {t( getPlanStatusKey( planStatus ))}
          </Badge>
        )}
      </div>

      <Collapse in={expanded}>
        <div className={classes.planContent}>
          {plan.steps.map(( step, index ) => {
            const result = executionResults?.[index];
            const status: StepExecutionStatus = result?.status || 'pending';
            const isHumanStepExecuting = step.storeName === '__human' && status === 'executing';
            const humanInstruction = isHumanStepExecuting
              ? ( step.payload as HumanStepPayload ).instruction
              : undefined;
            return (
              <div
                key={step.id}
                className={classes.planStep}
                data-testid={componentsTestIds.aiChatPlanStep( index )}
              >
                <div className={classes.stepRow}>
                  <span data-testid={componentsTestIds.aiChatPlanStatus( step.id )}>
                    <StepStatusIcon status={status} />
                  </span>
                  <span className={classes.stepDescription}>{step.description}</span>
                </div>
                {isHumanStepExecuting && (
                  <div className={classes.humanStepContent}>
                    {humanInstruction && (
                      <span className={classes.humanInstruction}>{humanInstruction}</span>
                    )}
                    <Button
                      size="compact-xs"
                      variant={colorScheme === 'dark' ? 'filled' : 'light'}
                      color="primary"
                      onClick={() => completeHumanStep( message.id )}
                    >
                      {t( 'ai_assistant_human_step_continue' )}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {planStatus === 'pending' && (
          <div className={classes.planActions}>
            <Button
              size="compact-xs"
              variant={colorScheme === 'dark' ? 'filled' : 'light'}
              color="primary"
              onClick={() => executePlan( message.id )}
            >
              {t( 'ai_assistant_apply_all' )}
            </Button>
            <Button
              size="compact-xs"
              variant="subtle"
              color="gray"
              onClick={() => discardPlan( message.id )}
            >
              {t( 'ai_assistant_discard' )}
            </Button>
          </div>
        )}

        {planStatus === 'executing' && (
          <div className={classes.planActions}>
            <Button
              size="compact-xs"
              variant="light"
              color="gray"
              leftSection={<IconPlayerStopFilled size={12} />}
              onClick={() => abortPlan( message.id )}
            >
              {t( 'ai_assistant_abort' )}
            </Button>
          </div>
        )}

        {isFinished && (
          <div className={classes.statusSummary}>
            {planStatus === 'completed' && t( 'ai_assistant_all_steps_completed' )}
            {planStatus === 'failed' && t( 'ai_assistant_some_steps_failed' )}
            {planStatus === 'aborted' && t( 'ai_assistant_execution_aborted' )}
            {planStatus === 'discarded' && t( 'ai_assistant_plan_was_discarded' )}
          </div>
        )}
      </Collapse>
    </div>
  );
}
